import React, { useRef } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { type SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { isAppLoading } from '@/core/features/loader';
import TextField from '@/components/TextField';
import SelectField from '@/components/SelectField';
import Form, { type FormElement } from '@/components/Form';
import { roleOptions, createMember } from '@/slices/members';
import { type RootState } from '@/store';
import { normalizeSimpleFormOptions, useYupValidationResolver } from '@/utils';
import { type DrawerContentProps } from '../../../state';
import { DrawerLayout } from '../../layouts';
import styles from './styles';

const validationSchema = yup.object({
  full_name: yup.string().required('Full name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
  role_id: yup.string().required('Role is required'),
});

const defaultValues: FormValues = {
  full_name: '',
  email: '',
  password: '',
  role_id: '',
};

type FormValues = {
  full_name: string;
  email: string;
  password: string;
  role_id?: string;
};

type Props = DrawerContentProps & ConnectedProps<typeof connector>;

const CreateMemberDrawer = ({
  closeDrawer,
  drawerData: { propertyId },
  roleOptions,
  isAppLoading,
  createMember,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = ({
    full_name,
    email,
    password,
    role_id,
  }) => {
    createMember({
      propertyId,
      member: {
        full_name,
        email,
        password,
        role_id: Number(role_id),
      },
    });
  };

  return (
    <DrawerLayout
      closeDrawer={closeDrawer}
      title="Create Member"
      onAccept={handleAccept}
      acceptText={isAppLoading ? 'Creating...' : 'Create'}
      acceptDisabled={isAppLoading}
      onCancel={closeDrawer}
      cancelText="Cancel"
    >
      <Form
        ref={formRef}
        sx={styles.form}
        resolver={resolver}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <TextField name="full_name" label="Full Name" />
        <TextField name="email" label="Email" type="email" />
        <TextField name="password" label="Password" type="password" />

        <SelectField
          name="role_id"
          label="Role"
          options={normalizeSimpleFormOptions(roleOptions)}
        />
      </Form>
    </DrawerLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    roleOptions: roleOptions(state),
    isAppLoading: isAppLoading(state),
  }),
  { createMember },
);

export default connector(CreateMemberDrawer);
